// Who filled each row of a setup card, and the card a setup shows before it has a draft (v3
// `orpheusSetupCardPresentation.ts`, KUR-8): the fields filled so far under the form's own rows,
// with the rows nothing has filled collapsed into one "still needed" line.
import type { SetupCard, SetupCardRow, SetupSlot } from '../models/orpheusSetup.types'

import {
  ALERT_ROW_SLOTS,
  ALERT_WRITE,
  humanize,
  isBlank,
  metricLabel,
  rowLabel,
  rowValue,
  textValue,
  type CardRowView,
  type RowSource,
  type SetupLabels,
} from './orpheusSetupCardRows'

/** Who filled a row: unmarked when the user said it, marked when inferred or still open. */
export function rowSource(capabilityId: string, row: SetupCardRow, slots: SetupSlot[]): RowSource {
  if (!slots.length) return null
  const fields =
    (capabilityId === ALERT_WRITE && ALERT_ROW_SLOTS[row.key]) || Object.keys(row.fields)
  const matched = slots.filter((slot) => fields.includes(slot.field))
  if (!matched.length) return null
  return slotSource(matched)
}

export function cardRows(
  labels: SetupLabels,
  card: SetupCard,
  slots: SetupSlot[] = [],
): CardRowView[] {
  return card.rows.map((row) => ({
    key: row.key,
    label: rowLabel(labels, card.capabilityId, row.key),
    value: rowValue(labels, card.capabilityId, row),
    source: rowSource(card.capabilityId, row, slots),
  }))
}

// What an alert can't be saved without; the rest take a default the confirm card shows
// (`common/feature_knowledge/alert_create/overview.md`).
const ALERT_REQUIRED_ROWS = ['metrics', 'trigger_alert_when', 'notification']

// Setup-memory fields that only an alert setup asks for.
const ALERT_ONLY_SLOTS = [
  'thresholds',
  'threshold',
  'direction',
  'threshold_type',
  'condition_operator',
  'compare_window',
  'channel_type',
]

// Fields the screen shows elsewhere or that mean nothing to a reader: the staged file's ref (its
// name and rows sit on the user's message and in the mapping table).
const HIDDEN_SLOTS = ['file_ref']

export interface IncompleteCard {
  rows: CardRowView[]
  /** The form labels of the rows nothing has filled yet. */
  stillNeeded: string[]
}

function slotSource(slots: SetupSlot[]): RowSource {
  if (slots.some((slot) => slot.source === 'missing')) return 'missing'
  if (slots.some((slot) => slot.source === 'system')) return 'guess'
  return 'user'
}

// A choice per file value arrives as `breakdown_value_choices.Meta`: the value names the row.
function slotLabel(field: string): string {
  const dot = field.lastIndexOf('.')
  return dot === -1 ? humanize(field) : field.slice(dot + 1) || humanize(field)
}

/**
 * A setup that has no draft yet, read from the fields filled so far. An alert's fields gather
 * under the form's own rows in its order; the rows nothing has filled collapse into one "still
 * needed" line, so the question stays in view.
 */
export function incompleteCard(labels: SetupLabels, allSlots: SetupSlot[]): IncompleteCard {
  const slots = allSlots.filter((slot) => !HIDDEN_SLOTS.includes(slot.field))
  const isAlert = slots.some((slot) => ALERT_ONLY_SLOTS.includes(slot.field))
  const groups: { key: string; label: string; slots: SetupSlot[] }[] = []
  const grouped = new Set<string>()

  if (isAlert) {
    for (const [key, fields] of Object.entries(ALERT_ROW_SLOTS)) {
      const rowSlots = slots.filter((slot) => fields.includes(slot.field))
      rowSlots.forEach((slot) => grouped.add(slot.field))
      if (rowSlots.length) {
        groups.push({ key, label: rowLabel(labels, ALERT_WRITE, key), slots: rowSlots })
      }
    }
  }
  for (const slot of slots.filter((candidate) => !grouped.has(candidate.field))) {
    groups.push({ key: slot.field, label: slotLabel(slot.field), slots: [slot] })
  }

  const rows: CardRowView[] = []
  const stillNeeded: string[] = []
  for (const group of groups) {
    const filled = group.slots.filter((slot) => slot.source !== 'missing' && !isBlank(slot.value))
    const first = filled[0]
    if (!first) {
      if (!isAlert || ALERT_REQUIRED_ROWS.includes(group.key)) stillNeeded.push(group.label)
      continue
    }
    const fields = Object.fromEntries(filled.map((slot) => [slot.field, slot.value]))
    const metricValue = /^metric_(columns|values)\./.test(group.key)
    rows.push({
      key: group.key,
      label: group.label,
      value: metricValue
        ? textValue(metricLabel(labels, first.value))
        : rowValue(labels, isAlert ? ALERT_WRITE : '', { key: group.key, fields }),
      source: slotSource(group.slots),
    })
  }
  return { rows, stillNeeded }
}

/** A setup turn shows its draft so far once at least one field is filled (KUR-8). */
export function hasSetupDraft(card: SetupCard | null, slots: SetupSlot[]): boolean {
  if (card) return true
  return slots.some((slot) => slot.source !== 'missing' && slot.value !== null)
}
